<template>
  <v-card class="section-card" elevation="0">
    <v-form ref="form" class="section-form">
      <div class="profile-hero">
        <div class="profile-hero-inner">
          <div class="avatar-wrapper" @click="onAvatarClick">
            <div class="avatar-border">
              <div class="avatar-circle">
                <v-img
                  v-if="currentAvatarUrl"
                  :key="currentAvatarUrl"
                  :src="currentAvatarUrl"
                  class="avatar-img"
                  contain
                />
                <template v-else>
                  <span v-if="initials" class="avatar-text">
                    {{ initials }}
                  </span>
                  <v-icon v-else size="52" color="#2563eb">
                    mdi-account
                  </v-icon>
                </template>
              </div>
            </div>

            <div class="avatar-edit">
              <v-icon small color="#ffffff">mdi-camera-plus</v-icon>
            </div>

            <input
              ref="avatarInput"
              type="file"
              accept="image/*"
              class="avatar-input"
              @change="onAvatarSelected"
            />
          </div>

          <div class="profile-info">
            <div class="profile-name">
              {{ fullName || form.username || "-" }}
            </div>

            <div v-if="form.username" class="profile-username">
              @{{ form.username }}
            </div>

            <div class="role-pill">
              <v-icon small class="role-icon">mdi-shield-check</v-icon>
              สิทธิ์การใช้งาน: {{ roleLabel }}
            </div>
          </div>

          <div class="meta-grid">
            <div class="meta-item">
              <v-icon size="18" class="meta-icon">
                mdi-clock-time-eight-outline
              </v-icon>
              <div>
                <div class="meta-label">เข้าใช้งานล่าสุด</div>
                <div class="meta-value">
                  {{ formattedLastLogin }}
                </div>
              </div>
            </div>

            <div class="meta-item">
              <v-icon size="18" class="meta-icon">mdi-calendar-plus</v-icon>
              <div>
                <div class="meta-label">สร้างบัญชีเมื่อ</div>
                <div class="meta-value">
                  {{ formattedCreatedAt }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="form-section">
        <v-row class="form-grid">
          <v-col cols="12">
            <span class="detail-text">ชื่อผู้ใช้</span>
            <v-text-field
              v-model.trim="form.username"
              placeholder="@username"
              v-bind="tfProps"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <span class="detail-text">ชื่อ</span>
            <v-text-field
              v-model.trim="form.firstName"
              placeholder="ชื่อ"
              v-bind="tfProps"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <span class="detail-text">นามสกุล</span>
            <v-text-field
              v-model.trim="form.lastName"
              placeholder="นามสกุล"
              v-bind="tfProps"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <span class="detail-text">อีเมล</span>
            <v-text-field
              v-model.trim="form.email"
              placeholder="อีเมล"
              v-bind="tfProps"
              :rules="emailRules"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <span class="detail-text">เบอร์โทรศัพท์</span>
            <v-text-field
              v-model.trim="form.phone"
              placeholder="เบอร์โทรศัพท์"
              v-bind="tfProps"
              :rules="phoneRules"
              counter="10"
              @input="onPhoneInput"
            />
          </v-col>

          <v-col cols="12">
            <span class="detail-text">ที่อยู่</span>
            <v-text-field
              v-model.trim="form.address"
              placeholder="ที่อยู่"
              v-bind="tfProps"
            />
          </v-col>
        </v-row>

        <div class="actions">
          <v-btn class="btn-secondary" height="44" outlined @click="reset">
            ยกเลิก
          </v-btn>
          <v-btn class="btn-primary" height="44" @click="save"> บันทึก </v-btn>
        </div>
      </div>
    </v-form>
  </v-card>
</template>

<script>
import swalAlert from "@/services/alert/swalServices";

const PHONE_REGEX = /^\d{10}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}(?:\.[A-Za-z]{2,})*$/;

export default {
  name: "ProfileInfoCard",
  props: {
    data: { type: Object, default: () => null },
  },

  data() {
    return {
      form: {
        id: "",
        username: "",
        email: "",
        firstName: "",
        lastName: "",
        phone: "",
        address: "",
        role: "",
        lastLoginAt: null,
        createdAt: null,
        avatarUrl: "",
      },
      phoneInvalid: false,
      originalData: null,
      tfProps: { outlined: true, hideDetails: "auto", dense: true },
      selectedAvatarFile: null,
      avatarPreviewUrl: "",
      phoneRules: [
        (v) => !!v || "กรุณาระบุเบอร์โทรศัพท์",
        (v) => /^\d+$/.test(v) || "กรุณาระบุตัวเลขเท่านั้น",
        (v) => v.length === 10 || "กรุณาระบุตัวเลข 10 หลัก",
      ],
      emailRules: [
        (v) =>
          !v ||
          EMAIL_REGEX.test(v) ||
          "กรุณากรอกรูปแบบอีเมลให้ถูกต้อง เช่น name@example.com",
      ],
    };
  },

  computed: {
    fullName() {
      return `${this.form.firstName || ""} ${this.form.lastName || ""}`.trim();
    },
    initials() {
      const f = (this.form.firstName || "").charAt(0);
      const l = (this.form.lastName || "").charAt(0);
      if (f || l) return (f + l).toUpperCase();
      if (this.form.username) return this.form.username.charAt(0).toUpperCase();
      return "";
    },
    roleLabel() {
      if (this.form.role === "superadmin") return "Super Admin";
      if (this.form.role === "admin") return "Admin";
      return this.form.role || "Unknown";
    },
    formattedLastLogin() {
      return this.formatThaiDateTime(this.form.lastLoginAt);
    },
    formattedCreatedAt() {
      return this.formatThaiDate(this.form.createdAt);
    },
    currentAvatarUrl() {
      if (this.avatarPreviewUrl) return this.avatarPreviewUrl;

      const raw = this.form.avatarUrl;
      if (!raw) return "";

      if (/^https?:\/\//i.test(raw) || raw.startsWith("blob:")) return raw;

      const base = process.env.VUE_APP_API_URL || "";
      if (!base) return raw;

      const cleanBase = base.replace(/\/+$/, "");
      const cleanPath = raw.startsWith("/") ? raw : `/${raw}`;
      return `${cleanBase}${cleanPath}`;
    },
  },

  watch: {
    data: {
      immediate: true,
      handler(v) {
        if (!v) return;
        this.form = {
          id: v.id || v._id || "",
          username: v.username || "",
          email: v.email || "",
          firstName: v.firstName || "",
          lastName: v.lastName || "",
          phone: v.phone || "",
          address: v.address || "",
          role: v.role || "",
          lastLoginAt: v.lastLoginAt || null,
          createdAt: v.createdAt || null,
          avatarUrl:
            v.avatarUrl ||
            v.avatar ||
            v.profileImage ||
            v.profile_image ||
            v.imageUrl ||
            v.image_url ||
            "",
        };
        this.originalData = JSON.parse(JSON.stringify(this.form));
      },
    },
  },

  methods: {
    formatThaiDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      return d.toLocaleDateString("th-TH", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    },
    formatThaiDateTime(date) {
      if (!date) return "-";
      const d = new Date(date);
      const datePart = this.formatThaiDate(date);
      const timePart = d.toLocaleTimeString("th-TH", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      return `${datePart} เวลา ${timePart} น.`;
    },

    async reset() {
      if (!this.originalData) return;

      const res = await swalAlert.Dialog(
        "ยกเลิกการแก้ไขข้อมูล",
        "คุณต้องการยืนยันยกเลิกการแก้ไขข้อมูลใช่หรือไม่?",
        require("@/assets/alert/cancel.png"),
        120,
        120,
        "ยืนยัน",
        "ยกเลิก",
        true
      );

      if (!res.isConfirmed) return;

      await swalAlert.Success("ยกเลิกแก้ไขโปรไฟล์สำเร็จ");

      this.form = JSON.parse(JSON.stringify(this.originalData));

      if (this.avatarPreviewUrl) URL.revokeObjectURL(this.avatarPreviewUrl);
      this.avatarPreviewUrl = "";
      this.selectedAvatarFile = null;
    },

    async save() {
      if (this.phoneInvalid) {
        await swalAlert.Error(
          "ไม่สามารถบันทึกข้อมูลได้",
          "เนื่องจากเบอร์โทรศัพท์ต้องเป็นตัวเลขเท่านั้น"
        );
        return;
      }

      const phone = (this.form.phone || "").trim();
      if (phone && !PHONE_REGEX.test(phone)) {
        await swalAlert.Error(
          "ไม่สามารถบันทึกข้อมูลได้",
          "ไม่สามารถบันทึกข้อมูลได้ เนื่องจากเบอร์โทรศัพท์ไม่ถูกต้อง"
        );
        return;
      }

      const email = (this.form.email || "").trim();
      if (email && !EMAIL_REGEX.test(email)) {
        await swalAlert.Error(
          "ไม่สามารถบันทึกข้อมูลได้",
          "ไม่สามารถบันทึกข้อมูลได้ เนื่องจากรูปแบบอีเมลไม่ถูกต้อง"
        );
        return;
      }

      const formRef = this.$refs.form;
      if (formRef?.validate && !formRef.validate()) return;

      this.$emit("save", { ...this.form });
    },

    onAvatarClick() {
      this.$refs.avatarInput && this.$refs.avatarInput.click();
    },

    onAvatarSelected(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;

      if (this.avatarPreviewUrl) URL.revokeObjectURL(this.avatarPreviewUrl);

      this.selectedAvatarFile = file;
      this.avatarPreviewUrl = URL.createObjectURL(file);

      this.$emit("avatar-change", file);
    },

    onPhoneInput(e) {
      const raw = e.target.value;
      const digitsOnly = raw.replace(/\D/g, "");
      this.phoneInvalid = raw !== digitsOnly;
      this.form.phone = digitsOnly.slice(0, 10);
    },
  },
};
</script>

<style scoped>
.section-card {
  border-radius: 18px;
  border: 1px solid #e5e7f0;
  background: #ffffff;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.03);
}
.section-form {
  padding: 20px 24px 20px;
}
.profile-hero {
  width: 100%;
  border-radius: 14px;
  padding: 24px 12px 20px;
  background: linear-gradient(180deg, #f7f9ff 0%, #ffffff 60%);
  border: 1px solid #eef2ff;
  margin-bottom: 12px;
}
.profile-hero-inner {
  max-width: 640px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.avatar-wrapper {
  position: relative;
  width: 140px;
  margin-bottom: 12px;
  cursor: pointer;
}
.avatar-border {
  width: 140px;
  height: 140px;
  border-radius: 999px;
  padding: 4px;
  background: #f4f6ff;
  border: 1px solid #d4ddff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.avatar-circle {
  background: #ffffff;
  width: 100%;
  height: 100%;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.avatar-text {
  font-size: 40px;
  font-weight: 700;
  color: #2563eb;
}
.avatar-edit {
  position: absolute;
  bottom: 6px;
  right: 10px;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: #2563eb;
  border: 2px solid #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 8px rgba(37, 99, 235, 0.35);
}
.avatar-input {
  display: none;
}
.profile-name {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
}
.profile-username {
  font-size: 13px;
  color: #9ca3af;
  margin-bottom: 6px;
}
.role-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  background: #eef2ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 500;
  margin-top: 6px;
}
.role-icon {
  color: #1d4ed8;
}
.meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 16px;
  width: 100%;
}
.meta-item {
  background: #ffffff;
  border-radius: 14px;
  padding: 10px 14px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border: 1px solid #e5e7f0;
}
.meta-icon {
  color: #2563eb;
  margin-top: 2px;
}
.meta-label {
  font-size: 11px;
  color: #9ca3af;
}
.meta-value {
  font-size: 13px;
  color: #0f172a;
  font-weight: 500;
}
.form-section {
  max-width: 900px;
  margin: 8px auto 0;
  border-top: 1px solid #edf0f4;
  padding-top: 18px;
}
.detail-text {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
}
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}
.btn-secondary {
  background: #ffffff !important;
  color: #4b5563 !important;
  border-radius: 10px !important;
  min-width: 120px;
  border: 1px solid #d1d5db !important;
}
.btn-primary {
  background: #2563eb !important;
  color: #ffffff !important;
  border-radius: 10px !important;
  min-width: 140px;
}
:deep(.v-input) {
  margin-bottom: 10px !important;
}
:deep(.v-input--dense .v-input__slot) {
  min-height: 44px !important;
  height: 44px !important;
  padding: 0 10px !important;
  border-radius: 10px !important;
  border-color: #e5e7eb !important;
  background: #ffffff !important;
}
:deep(.v-text-field.v-input--dense input) {
  height: 24px !important;
  line-height: 24px !important;
  padding: 0 !important;
  font-size: 14px !important;
}
@media (max-width: 600px) {
  .section-form {
    padding: 18px 14px 18px;
  }
  .avatar-wrapper {
    width: 120px;
  }
  .avatar-border {
    width: 120px;
    height: 120px;
  }
  .avatar-text {
    font-size: 34px;
  }
  .meta-grid {
    grid-template-columns: 1fr;
  }
  .form-section {
    padding-top: 16px;
  }
}
</style>